import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { getBooking, getItem, imageUrl, reportIssue, requestExtension } from '../api';
import BookingTimeline from '../components/BookingTimeline';
import ContactOwnerSheet from '../components/ContactOwnerSheet';
import CancelBookingModal from '../components/CancelBookingModal';
import CancellationDetailsModal from '../components/CancellationDetailsModal';
import InfoTip from '../components/InfoTip';
import { DEPOSIT_STATUS_HELP } from '../components/statusHelp';

const STATUS_VARIANT = { confirmed: 'success', cancelled: 'danger', pending: 'warning' };

// Full booking details page (MyBookings user story 2.1): Jewellery / Rental / Delivery / Payment
// sections, the status timeline, and customer self-service actions (3.2).
export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showCancellationDetails, setShowCancellationDetails] = useState(false); // Story 12
  const [showExtend, setShowExtend] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [extendNote, setExtendNote] = useState('');
  const [issueNote, setIssueNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await getBooking(id);
      setBooking(b);
      const i = await getItem(b.itemId);
      setItem(i);
    } catch (err) {
      // Surface the real reason (e.g. rate-limited, network error) instead of always claiming the
      // booking doesn't exist - a 429/500 here previously showed the same "not found" message as
      // an actual missing booking, which is misleading and hard to diagnose.
      if (err.response?.status === 404) setError('Booking not found.');
      else setError(err.response?.data?.msg || 'Could not load this booking. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancelled = (result) => {
    setShowCancel(false);
    setBooking(result.data);
    const refunded = result.refundBreakdown
      ? `₹${result.refundBreakdown.rentalAmountRefunded + result.refundBreakdown.deliveryChargeRefunded + result.refundBreakdown.depositRefunded} refunded.`
      : '';
    setActionMsg(
      result.settlementPending
        ? `Booking cancelled - your refund is under Finance review and will be confirmed shortly.`
        : `Booking cancelled. ${refunded}`
    );
  };

  const handleExtend = async (e) => {
    e.preventDefault();
    setBusy(true);
    setActionErr('');
    try {
      await requestExtension(id, { requestedEndDate: extendDate, note: extendNote });
      setActionMsg('Extension request sent to the owner.');
      setShowExtend(false);
      load();
    } catch (err) {
      setActionErr(err.response?.data?.msg || 'Unable to request an extension.');
    } finally {
      setBusy(false);
    }
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    setBusy(true);
    setActionErr('');
    try {
      await reportIssue(id, { note: issueNote });
      setActionMsg('Thanks - support has been notified.');
      setShowIssue(false);
      setIssueNote('');
    } catch (err) {
      setActionErr(err.response?.data?.msg || 'Unable to report the issue.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!booking) return null;

  const canCancel = ['pending', 'confirmed'].includes(booking.status) && new Date(booking.startDate) > new Date();
  const canExtend = booking.status === 'confirmed' && !booking.extension?.requested;

  return (
    <div>
      <Button variant="link" className="px-0 mb-3" onClick={() => navigate('/my-bookings')}>
        &larr; Back to my bookings
      </Button>

      {actionMsg && <Alert variant="success" dismissible onClose={() => setActionMsg('')}>{actionMsg}</Alert>}
      {actionErr && <Alert variant="danger" dismissible onClose={() => setActionErr('')}>{actionErr}</Alert>}

      <Row className="g-4">
        <Col md={7}>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h4 className="mb-1">{item?.name || 'Jewellery item'}</h4>
                  <div className="text-muted small">Booking #{booking._id.slice(-6).toUpperCase()}</div>
                </div>
                <Badge bg={STATUS_VARIANT[booking.status] || 'secondary'}>{booking.status}</Badge>
              </div>

              {item?.images?.[0] && (
                <img src={imageUrl(item.images[0])} alt={item.name} className="w-100 rounded my-3" style={{ maxHeight: 260, objectFit: 'cover' }} />
              )}

              <h6 className="text-uppercase text-muted small mt-3">Jewellery</h6>
              <p className="mb-1">{item?.description}</p>
              <p className="mb-1 small text-muted">
                {item?.attributes?.metal && <>Material: {item.attributes.metal} &middot; </>}
                Owner: {item?.attributes?.storeName || 'Independent seller'}
              </p>

              <h6 className="text-uppercase text-muted small mt-3">Rental</h6>
              <p className="mb-1">
                {new Date(booking.startDate).toLocaleDateString()} &ndash; {new Date(booking.endDate).toLocaleDateString()}
              </p>

              <h6 className="text-uppercase text-muted small mt-3">Delivery</h6>
              <p className="mb-1">{booking.customerName} &middot; {booking.phoneNumber}</p>
              <p className="mb-1 small text-muted">Delivery status: {booking.deliveryStatus.replace(/_/g, ' ')}</p>
              {booking.notes && <p className="mb-1 small">Instructions: {booking.notes}</p>}
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3">Payment</h6>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr><td>Rental amount</td><td className="text-end">₹{booking.rentalAmount.toLocaleString('en-IN')}</td></tr>
                  <tr><td>Platform fee</td><td className="text-end">₹{booking.platformFee.toLocaleString('en-IN')}</td></tr>
                  <tr><td>Delivery</td><td className="text-end">₹{booking.deliveryCharge.toLocaleString('en-IN')}</td></tr>
                  {booking.depositAmount > 0 && (
                    <tr><td>Security deposit</td><td className="text-end">₹{booking.depositAmount.toLocaleString('en-IN')}</td></tr>
                  )}
                  <tr className="fw-bold border-top">
                    <td>{booking.paymentSummary?.received ? 'Total paid' : 'Total payable'}</td>
                    <td className="text-end">₹{booking.totalAmount.toLocaleString('en-IN')}</td>
                  </tr>
                  {/* tax_and_gst_calculation.md - the GST already inside what you paid. */}
                  {booking.taxBreakdown?.customerGst?.gstTotal != null && (
                    <tr><td>of which GST</td><td className="text-end">₹{booking.taxBreakdown.customerGst.gstTotal.toLocaleString('en-IN')}</td></tr>
                  )}
                  {booking.depositAmount > 0 && (
                    <tr>
                      <td>Deposit / refund status<InfoTip text={DEPOSIT_STATUS_HELP[booking.depositStatus]} /></td>
                      <td className="text-end">{booking.depositStatus.replace(/_/g, ' ')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* tax_and_gst_calculation.md - how and when you paid. */}
              {booking.paymentSummary && (
                <div className="small text-muted mt-2">
                  {booking.paymentSummary.received
                    ? `Paid${booking.paymentSummary.method ? ` via ${booking.paymentSummary.method.replace(/_/g, ' ')}` : ''}${booking.paymentSummary.receivedAt ? ` on ${new Date(booking.paymentSummary.receivedAt).toLocaleString()}` : ''}`
                    : booking.paymentSummary.label}
                  {booking.taxBreakdown?.invoiceNumber && <> &middot; Invoice {booking.taxBreakdown.invoiceNumber}</>}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={5}>
          <Card className="mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3">Status</h6>
              <BookingTimeline booking={booking} />
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="d-flex flex-column gap-2">
              <h6 className="text-uppercase text-muted small mb-1">Actions</h6>
              <Button variant="outline-dark" onClick={() => setShowContact(true)}>Contact owner</Button>
              {canExtend && (
                <Button variant="outline-secondary" onClick={() => setShowExtend((v) => !v)}>Request extension</Button>
              )}
              {showExtend && (
                <Form onSubmit={handleExtend} className="border rounded p-2">
                  <Form.Group className="mb-2">
                    <Form.Label className="small">New end date</Form.Label>
                    <Form.Control type="date" required min={booking.endDate.slice(0, 10)} value={extendDate} onChange={(e) => setExtendDate(e.target.value)} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small">Note (optional)</Form.Label>
                    <Form.Control as="textarea" rows={2} value={extendNote} onChange={(e) => setExtendNote(e.target.value)} />
                  </Form.Group>
                  <Button size="sm" type="submit" disabled={busy}>Send request</Button>
                </Form>
              )}
              {booking.extension?.requested && (
                <Alert variant="info" className="py-2 small mb-0">
                  Extension request ({booking.extension.status}) to {new Date(booking.extension.requestedEndDate).toLocaleDateString()}
                </Alert>
              )}

              <Button variant="outline-secondary" onClick={() => setShowIssue((v) => !v)}>Report an issue</Button>
              {showIssue && (
                <Form onSubmit={handleIssue} className="border rounded p-2">
                  <Form.Group className="mb-2">
                    <Form.Label className="small">What went wrong?</Form.Label>
                    <Form.Control as="textarea" rows={2} required value={issueNote} onChange={(e) => setIssueNote(e.target.value)} />
                  </Form.Group>
                  <Button size="sm" type="submit" disabled={busy}>Submit</Button>
                </Form>
              )}

              {item && (
                <Button as={Link} to={`/items/${item._id}`} variant="outline-primary">
                  Re-rent this item
                </Button>
              )}

              {canCancel && (
                <Button variant="outline-danger" onClick={() => setShowCancel(true)} disabled={busy}>
                  Cancel booking
                </Button>
              )}
              {booking.status === 'cancelled' && (
                // Owner Bookings & Cancellation v2 doc, Story 12 - the customer can see the same
                // refund/settlement status + Finance comments the owner sees for their own booking.
                <Button variant="outline-secondary" onClick={() => setShowCancellationDetails(true)}>
                  View cancellation details
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ContactOwnerSheet item={item} show={showContact} onHide={() => setShowContact(false)} />
      <CancelBookingModal bookingId={id} show={showCancel} onHide={() => setShowCancel(false)} onCancelled={handleCancelled} />
      <CancellationDetailsModal booking={booking} show={showCancellationDetails} onHide={() => setShowCancellationDetails(false)} />
    </div>
  );
}
